<script setup>
import Icon from 'dashboard/components-next/icon/Icon.vue';

defineProps({
  options: {
    type: Array,
    default: () => [],
    validator: options =>
      options.every(
        opt => typeof opt === 'object' && 'value' in opt && 'label' in opt
      ),
  },
  groups: {
    type: Array,
    default: () => [],
    validator: groups =>
      groups.every(
        group =>
          'label' in group &&
          Array.isArray(group.options) &&
          group.options.every(opt => 'value' in opt && 'label' in opt)
      ),
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  // Settings forms lay their fields out full width. The wrapper defaults to
  // w-fit for the filter-bar and card usages that shipped first, so widening
  // is opt-in and no existing caller changes shape.
  fullWidth: {
    type: Boolean,
    default: false,
  },
});

const modelValue = defineModel({
  type: [String, Number, Boolean],
  default: '',
});

defineOptions({
  inheritAttrs: false,
});
</script>

<template>
  <div class="relative" :class="fullWidth ? 'w-full' : 'w-fit'">
    <select
      v-bind="$attrs"
      v-model="modelValue"
      :disabled="disabled"
      class="appearance-none bg-none rounded-dc-field border-0 outline-1 outline -outline-offset-1 transition-all duration-dc-base ease-dc-standard focus-visible:outline-dc-focus focus-visible:outline-offset-dc-focus bg-n-surface-1 !mb-0 py-2 px-3 pr-10 text-sm"
      :class="{
        'outline-n-weak hover:outline-n-slate-6 focus:outline-n-blue-9':
          !error && !disabled,
        'outline-n-ruby-9 focus:outline-n-ruby-9': error && !disabled,
        'outline-n-weak bg-n-slate-2 cursor-not-allowed opacity-60': disabled,
        'w-full': fullWidth,
      }"
    >
      <option v-if="placeholder" value="" disabled>
        {{ placeholder }}
      </option>
      <template v-if="groups.length">
        <optgroup
          v-for="group in groups"
          :key="group.label"
          :label="group.label"
        >
          <option
            v-for="option in group.options"
            :key="option.value"
            :value="option.value"
            :disabled="option.disabled"
          >
            {{ option.label }}
          </option>
        </optgroup>
      </template>
      <template v-else>
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </template>
    </select>
    <div
      class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
    >
      <Icon
        icon="i-lucide-chevron-down"
        class="size-4 text-n-slate-11"
        :class="{ 'opacity-50': disabled }"
      />
    </div>
  </div>
</template>
